import {
  ChevronRight,
  ChevronsUpDown,
  CreditCard,
  KeyRound,
  LogOut,
  UserRound,
} from 'lucide-react';
// @ts-ignore
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  Typography,
  useSidebar,
} from '@growdash/design-system/atoms';

import GrowdashLogo from '@growdash/design-system/logo/PNGs/Growdash - logo - full colour.png';
import GrowdashLogoWhite from '@growdash/design-system/logo/PNGs/Growdash - logo - Reversed White.png';
import GrowintelLogoWhite from '../../assets/logo/logo-growintel-white.png';
import GrowintelLogo from '../../assets/logo/logo-growintel.png';
import SmallGrowintelLogoWhite from '../../assets/logo/small-growintel-white.png';
import SmallGrowintelLogo from '../../assets/logo/small-growintel.png';

import { cn } from '@growdash/design-system/utils/helpers.js';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useRouter } from '../../hooks/useRouter';
import { useTabs } from '../../hooks/useTabs';

interface Props {
  user: any;
  handleLogout: VoidFunction;
  handleChangePassword: VoidFunction;
  handleChangeUserName?: VoidFunction;
  handleCreditNotes: VoidFunction;
  isManager?: boolean;
  activeTab: string;
  disabled?: boolean;
}

export enum ProductType {
  GROWINTEL = 'growintel',
  GROWDASH = 'growdash',
  GROWINTEL_PRO = 'growintel.pro',
}

export default function GrowintelSidebar({
  user,
  handleLogout,
  handleChangePassword,
  handleCreditNotes,
  isManager,
  activeTab,
  disabled,
}: Props) {
  const team = {
    name: 'Growdash',
  };

  const router = useRouter();

  const { open, setOpen } = useSidebar();
  const { tabs } = useTabs();
  const growdashEnabled =
    user?.products.includes(ProductType.GROWDASH) || isManager;

  useEffect(() => {
    setOpen(false);
  }, []);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="bg-background border-b group-data-[collapsible=icon]:border-b-0">
        <SidebarMenu>
          <SidebarMenuItem className="flex justify-center">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton size="lg">
                  {open ? (
                    <>
                      <img
                        src={GrowintelLogo}
                        className="dark:hidden w-[86%]"
                        referrerPolicy="no-referrer"
                      />
                      <img
                        src={GrowintelLogoWhite}
                        className="hidden dark:block w-[86%]"
                        referrerPolicy="no-referrer"
                      />
                    </>
                  ) : (
                    <div className="animate-fade-in flex w-[2.125rem] h-[2.125rem] items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                      <img
                        src={SmallGrowintelLogo}
                        className="dark:hidden min-w-full h-auto"
                        alt={team.name}
                        referrerPolicy="no-referrer"
                      />
                      <img
                        src={SmallGrowintelLogoWhite}
                        className="hidden dark:block min-w-full h-auto"
                        alt={team.name}
                        referrerPolicy="no-referrer"
                      />
                    </div>
                  )}
                  {open && <ChevronsUpDown className="ml-1" />}
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              {growdashEnabled && (
                <DropdownMenuContent
                  className="min-w-[--radix-dropdown-menu-trigger-width] w-64 rounded-lg cursor-pointer flex items-center p-2 hover:bg-muted"
                  side="bottom"
                  align="end"
                  sideOffset={4}
                >
                  <Link to={'/'}>
                    <SidebarMenuButton
                      asChild
                      size="lg"
                      className=" w-full flex gap-4"
                    >
                      <div className="flex items-center  max-w-full">
                        <img
                          className="w-[84%] dark:hidden"
                          src={GrowdashLogo}
                          alt={team.name}
                          referrerPolicy="no-referrer"
                        />
                        <img
                          className="w-[84%] hidden dark:block"
                          src={GrowdashLogoWhite}
                          alt={team.name}
                          referrerPolicy="no-referrer"
                        />

                        <ChevronRight />
                      </div>
                    </SidebarMenuButton>
                  </Link>
                </DropdownMenuContent>
              )}
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="bg-background">
        <SidebarGroup>
          <SidebarMenu>
            {tabs.map((item, index) => {
              const isActive = item.path === activeTab;
              return (
                <Link
                  className={cn('hover:no-underline', {
                    'flex justify-center': !open,
                  })}
                  key={index}
                  to={disabled ? '#' : item.path}
                >
                  <SidebarMenuItem className="flex">
                    <SidebarMenuButton
                      disabled={disabled}
                      isActive={isActive}
                      className={cn(
                        'transition-colors duration-200 min-h-12 flex items-center group-data-[collapsible=icon]:!p-1',
                        {
                          'transition-all hover:translate-x-0.5 hover:bg-gradient-purple':
                            open,
                          'translate-x-0.5 bg-gradient-purple':
                            isActive && open,
                        },
                      )}
                      tooltip={item.title}
                    >
                      <div>
                        {item.icon && (
                          <item.icon
                            className={cn('text-tertiary', {
                              'fill-bg_primary [&>path]:fill-bg_primary [&>path]:text-bg_primary dark:[&>path]:text-primary dark:[&>path]:fill-text_primary':
                                isActive,
                            })}
                          />
                        )}
                      </div>
                      <Typography
                        data-state={isActive ? 'active' : ''}
                        className="capitalize text-[1rem] data-[state=active]:text-bg_primary dark:data-[state=active]:text-primary"
                      >
                        {item.title}
                      </Typography>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </Link>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="bg-background">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground border border-border"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    {/* <AvatarImage src={user.avatar} alt={user.name} /> */}
                    <AvatarFallback className="rounded-lg">
                      {user.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{user.name}</span>
                    <span className="truncate text-xs">{user.username}</span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="bottom"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarImage src={user.avatar} alt={user.name} />
                      <AvatarFallback className="rounded-lg">
                        {user.name?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {user.name}
                      </span>
                      <span className="truncate text-xs">{user.username}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                {isManager && (
                  <>
                    <DropdownMenuItem onClick={handleCreditNotes}>
                      <CreditCard />
                      Credit Notes
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  onSelect={() => router.push('/grow-intel/profile')}
                >
                  <UserRound />
                  Profile
                </DropdownMenuItem>

                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleChangePassword}>
                  <KeyRound />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
